import client from "./client";
import LocalStorage from 'Core/commons/LocalStorage';
import { debounce } from "lodash";
import { computeCart, generateQuantity, fetchMetadata, persistCart } from 'api/sellsyaccountmanager';
import { licencesProducts, bonusPacks, groupedPacks } from 'Config/authenticated/LicencesData';

export default {
    namespaced: true,
    state() {
        return {
            ...client.state(),
            context: 'upsale',
            localStorage: new LocalStorage('cart', 'upsell.save'),
            showAdvAlert: false,
            advAlertAlreadyShown: false,
            previousCart: {
                paymentMethod: null,
                products: {},
                serviceEndDate: '',
                card: {
                    cardNumber: false,
                    cardExpiry: false,
                    cardCvc: false,
                    cardName: null
                },
                plan: '',
                total: {
                    monthly: {
                        total_amount: null,
                        total_amount_discount: null,
                        total_amount_discounted: null,
                        total_amount_tax_included: null,
                        total_amount_vat: null
                    },
                    yearly: {
                        total_amount: null,
                        total_amount_discount: null,
                        total_amount_discounted: null,
                        total_amount_tax_included: null,
                        total_amount_vat: null
                    }
                },
                various: {
                    monthly: {
                        total_amount: null,
                        total_amount_discount: null,
                        total_amount_discounted: null,
                        total_amount_tax_included: null,
                        total_amount_vat: null,
                    },
                    yearly: {
                        total_amount: null,
                        total_amount_discount: null,
                        total_amount_discounted: null,
                        total_amount_tax_included: null,
                        total_amount_vat: null,
                    }
                },
                taxRate: null,
            },
            previousCurrentPlan : '',
            totalProrated: {
                monthly: {
                    total_amount: null,
                    total_tax_included: null
                },
                yearly: {
                    total_amount: null,
                    total_tax_included: null
                }
            }
        }
    },
    getters: {
        ...client.getters,
        getLocalStorage: state => {
            return state.localStorage.get();
        },
        canGoToPayment: (state, getters) => {
            return getters.quantityPaidLicences >= 2;
        },
        previousCart: state => {
            return state.previousCart;
        },
        // Previous Products has always licences
        previousProductsWithLicences: (state, getters) => {
            return getters.availablesProducts.filter(product => {
                return !_.isNil(state.previousCart.products[product.id]) && state.previousCart.products[product.id].quantity !== 0
            });
        },
        previousPacksWithLicences: (state, getters) => {
            return [...getters.availablesPacks, ...getters.availablesGroupedPacks].filter(product => {
                return !_.isNil(state.previousCart.products[product.id]) && getters.previousCart.products[product.id].quantity !== 0;
            });
        },
        previousProducts: state => {
            return state.previousCart.products;
        },
        previousFreeLicencesByProduct: state => id => {
            return _.cloneDeep(state.previousCart.products[id]?.customPricing.find(price => price.unit_amount === 0)?.qt);
        },
        /**
         * Get entire plan label: ESSENTIAL_YEARLY 
         */
        previousPlan: state => {
            return state.previousCart.plan;
        },
        /**
         * Get plan first part (id / code): ESSENTIAL
         */
        previousCurrentPlan: state => {
            return _.first(state.previousCart?.plan?.split('_'));
        },
        /**
         * Get plan first part(label / name): ESSENTIALS
         */
        previousCurrentPlanLabel: (state, getters) => {
            return state.plans?.find(plan => plan.code ===  getters.previousCurrentPlan)?.name.toUpperCase()
        },
        /**
         * Get plan last part: YEARLY
         */
        previousRecurrency: state => {
            return _.last(state.previousCart?.plan?.split('_'));
        },
        isPreviouslyYearly: (state, getters) => {
            return getters.previousRecurrency === 'YEARLY';
        },
        isPreviouslyMonthly: (state, getters) => {
            return getters.previousRecurrency === 'MONTHLY';
        },
        previousPaymentMethod: state => {
            return state.previousCart.paymentMethod;
        },
        previousPaymentMethodLabel: state => {
            return state.paymentMethods.find(method => method.id === state.previousCart.paymentMethod)?.label;
        },
        previousTotalYearly: state => {
            return state.previousCart.total.yearly;
        },
        previousTotalMonthly: state => {
            return state.previousCart.total.monthly;
        },
        previousTaxRate: state => {
            return state.previousCart.taxRate;
        },
        variousTotalYearly: state => {
            return state.previousCart.various.yearly;
        },
        variousTotalMonthly: state => {
            return state.previousCart.various.monthly;
        },
        isADV: state => {
            return state.permissions.editCartWithNoChangeOnSubscriptionTotalAmount;
        },
        canSubmitUpsell: (state, getters) => {
            if(getters.isADV) {
                return state.previousCart.total.yearly.total_amount_discounted > state.total.yearly.total_amount_discounted;
            }
            if(getters.advAlertAlreadyShown) {
                return state.previousCart.total.yearly.total_amount_discounted >= state.total.yearly.total_amount_discounted;
            }
            return state.previousCart.total.yearly.total_amount_discounted < state.total.yearly.total_amount_discounted;
        },
        upsellIsLowest: (state, getters) => {
            if (getters.advAlertAlreadyShown) return state.previousCart.total.yearly.total_amount_discounted >= state.total.yearly.total_amount_discounted;
            return state.previousCart.total.yearly.total_amount_discounted > state.total.yearly.total_amount_discounted
        },
        alertAdv: state => {
            return state.showAdvAlert;
        },
        advAlertAlreadyShown: state => {
            return state.advAlertAlreadyShown;
        },
        totalProrated: state => {
            return state.totalProrated;
        },
        isLatestPricingVersion: state => {
            return state.previousPricingVersion === 4;
        },
    },
    mutations: {
        ...client.mutations,
        setLocalStorage(state, payload) {
            state.localStorage.set(payload)
        },
        copyToPreviousCart(state, products) {
            if(_.isNil(state.cartId)) state.products = products;
            state.card = state.previousCart.card;
        },
        setPreviousProducts(state, payload) {
            state.previousCart.products = payload;
        },
        setServiceEndDate(state, payload) {
            state.previousCart.serviceEndDate = payload;
        },
        setPreviousCartTotal(state, payload) {
            state.previousCart.total = payload;
        },
        setPreviousPlan(state, payload) {
            state.previousCart.plan = payload;
        },
        setPreviousPaymentMethod(state, payload) {
            state.previousCart.paymentMethod = payload;
        },
        setPreviousTaxRate(state, payload) {
            state.previousCart.taxRate = payload;
        },
        setVariousTotal(state) {
            state.previousCart.various = {
                monthly: {
                    total_amount: -(state.previousCart.total.monthly.total_amount - state.total.monthly.total_amount),
                    total_amount_discount: -(state.total.monthly.total_amount_discount - state.previousCart.total.monthly.total_amount_discount),
                    total_amount_discounted: -(state.previousCart.total.monthly.total_amount_discounted - state.total.monthly.total_amount_discounted),
                    total_amount_tax_included: -(state.previousCart.total.monthly.total_amount_tax_included - state.total.monthly.total_amount_tax_included),
                    total_amount_vat: -(state.previousCart.total.monthly.total_amount_vat - state.total.monthly.total_amount_vat),
                },
                yearly: {
                    total_amount: -(state.previousCart.total.yearly.total_amount - state.total.yearly.total_amount),
                    total_amount_discount: -(state.total.yearly.total_amount_discount - state.previousCart.total.yearly.total_amount_discount),
                    total_amount_discounted: -(state.previousCart.total.yearly.total_amount_discounted - state.total.yearly.total_amount_discounted),
                    total_amount_tax_included: -(state.previousCart.total.yearly.total_amount_tax_included - state.total.yearly.total_amount_tax_included),
                    total_amount_vat: -(state.previousCart.total.yearly.total_amount_vat - state.total.yearly.total_amount_vat),
                }
            };
        },
        setAdvAlertVisibility(state, payload) {
            state.showAdvAlert = state.advAlertAlreadyShown ? state.showAdvAlert : payload;
        },
        markAdvAlertShown(state) {
            state.advAlertAlreadyShown = true;
        },
        setCartTotalProrated(state, payload) {
            state.totalProrated = payload;
        },
    },
    actions: {
        ...client.actions,
        computeCart: debounce(({ state, getters, commit }) => {
            commit('setComputeLoading', true);
            const payload = computeCart(state);
            if (!payload.items.length) {
                commit('setComputeLoading', false); 
                return;
            }
            return new Promise((resolve, reject) => {
                SipClient.post(`/customers/${getters.customerId}/compute-cart`, { pricingversion: 4 },
                    {
                        ...payload,
                        country: _.isNil(payload.country) ? 'FR' : payload.country,
                    })
                    .then(res => {
                        const products = getters.products;
                        _.each(getters.products, (product, productId) => {
                            if (!res.data.products[productId]) return;
                            products[productId] = {
                                ...products[productId],
                                total: {
                                    monthly: res.data.products[productId].monthly,
                                    yearly: res.data.products[productId].yearly,
                                }
                            };
                        });
                        commit('setTaxRate', res.data.tax_rate);
                        commit('setProducts', products);
                        commit('setCartTotal', { monthly: res.data.monthly, yearly: res.data.yearly });
                        commit('setVariousTotal');
                        commit('setCartTotalProrated', {
                            monthly: {
                                total_amount: res.data.totalMonthlyProrationTaxesFree,
                                total_tax_included: res.data.totalMonthlyProrationAllInc,
                            },
                            yearly: {
                                total_amount: res.data.totalProrationTaxesFree,
                                total_tax_included: res.data.totalProrationAllInc,
                            }
                        });
                        commit('setLocalStorage', {
                            customerId: getters.customerId,
                            plan: getters.currentPlan,
                            recurrency: getters.recurrency,
                            products: getters.products
                        });
                        resolve();
                    }).catch(err => {
                        reject(err);
                    }).finally(() => {
                        commit('setComputeLoading', false);
                    });
            });
        }, 250),
        computePreviousCart: debounce(({ state, getters, commit }) => {
            const payload = computeCart({ 
                ...state,
                products: getters.previousProducts,
                plan: getters.previousPlan
            });
            return new Promise((resolve, reject) => {
                SipClient.post(`/customers/${getters.customerId}/compute-cart`, {},
                    {
                        ...payload,
                        country: _.isNil(payload.country) ? 'FR' : payload.country,
                    })
                    .then(res => {
                        const products = getters.previousProducts;
                        _.each(getters.previousProducts, (product, productId) => {
                            if (!res.data.products[productId]) return;
                            products[productId] = {
                                ...products[productId],
                                total: {
                                    monthly: res.data.products[productId].monthly,
                                    yearly: res.data.products[productId].yearly,
                                }
                            };
                        });
                        commit('setPreviousTaxRate', res.data.tax_rate);
                        commit('setPreviousProducts', products);
                        // Get total for Upsell Cart and Previous Total Cart
                        commit('setPreviousCartTotal', { monthly: res.data.monthly, yearly: res.data.yearly });
                        commit('setVariousTotal');
                        resolve();
                    }).catch(err => {
                        reject(err);
                    }).finally(() => {
                        commit('setComputeLoading', false);
                    });
            });
        }),
        createCart({ state, commit }) {
            commit('setComputeLoading', true);
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.post(`/rest/sellsy-account-manager/cart`, {}, persistCart(state),
                    (res) => {
                        if (res.error) reject(res);
                        commit('setComputeLoading', false);
                        commit('setCartId', res.id);
                        resolve(res);
                    }, (err) => {
                        commit('setComputeLoading', false);
                        reject(err.response.data.error.message);
                    });
            });
        },
        updateCart({ state, commit, getters }, payload) {
            commit('setComputeLoading', true);
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.put(`/rest/sellsy-account-manager/cart/${payload}`, { user_context: getters.userContext }, persistCart(state),
                    (res) => {
                        commit('setComputeLoading', false);
                        resolve();
                    }, (err) => {
                        commit('setComputeLoading', false);
                        reject(err.response.data.error.message);
                    });
            });
        },
        fetchMetadata({ commit, dispatch, getters }, payload) {
            commit('setComputeLoading', true);
            fetchMetadata(getters.customerId, getters.context, getters.termsType).then(res => {
                dispatch('setMetadata', res);
                dispatch('fetchCustomerData', payload);
            });
        },
        setPreviousCart({ commit, state }, payload) {
            commit('setServiceEndDate', payload.billingSettings.serviceEndDate);
            commit('copyToPreviousCart', { ...state.products, ..._.cloneDeep(state.previousCart.products) });
        },
        updatePreviousProducts({ commit, getters }, payload) {
            let products = {};

            getters.allAvailablesProducts.forEach(availableProduct => {
                const subscribedProduct = payload.find(item => item.code === availableProduct.code);
                if (!_.isNil(subscribedProduct)) {
                    products = {
                        ...products,
                        [subscribedProduct.productPlan.product.uuid]: generateQuantity({
                            customPricing: _.isNil(subscribedProduct.productPlan.customPricing)
                                ? [{ 
                                    up_to: 'inf', 
                                    unit_amount: availableProduct.plans.find(plan => plan.code === getters.previousPlan).amountInCents
                                }]
                                : subscribedProduct.productPlan.customPricing,
                            // bonusPack didn't have quantity by default, need to set at 1.
                            quantity: Object.keys({...bonusPacks, ...groupedPacks}).includes(subscribedProduct.code) ? 1 : subscribedProduct.customerLicense.quantitySubscribed,
                            // Only for Sellsy Sale use
                            availablesLicences: Object.keys({ ...bonusPacks, ...groupedPacks }).includes(subscribedProduct.code) ? 1 : subscribedProduct.customerLicense.quantitySubscribed - subscribedProduct.customerLicense.quantityFree
                        }, getters.userIsClient)
                    }
                }
            });

            commit('setPreviousProducts', products);
        },

        fetchCustomerData({ commit, dispatch, state, getters }, payload) {
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.get(`/rest/sellsy-account-manager/corporation/${payload}/datas`, {}, response => {
                    try {
                        if (_.isNil(getters.cartId)) {
                            commit('setPaymentMethod', response.data.billingSettings.paymentMethod);
                            dispatch('updatePlan', response.data.billingSettings.pricingPlanCode.split('_')[0]);
                            dispatch('updateRecurrency', response.data.billingSettings.pricingPlanCode.split('_')[1]);
                        }
                        commit('setPreviousPricingVersion', response.data.billingSettings.pricingVersion);
                        commit('setPreviousPlan', response.data.billingSettings.pricingPlanCode);
                        commit('setPreviousPaymentMethod', response.data.billingSettings.paymentMethod);
                        commit('setPeriodApplianceStart', response.data.billingSettings.serviceStartDate);
                        commit('setAccountLevel', response.data.levelReadable);
                        dispatch('updatePreviousProducts', response.subscribedProducts
                            .filter(product => Object.keys({ ...licencesProducts, ...bonusPacks, ...groupedPacks})
                                .includes(product.code) && !_.isNil(product.productPlan)));
                        dispatch('setPreviousCart', response);
                        dispatch('computeCart');
                        dispatch('computePreviousCart');
                        resolve(response);
                    } catch(err) {
                        reject(err);
                    }
                });
            });
        },
        closeAdvAlert({ commit }) {
            commit('setAdvAlertVisibility', false);
            commit('markAdvAlertShown');
        }
    },
}